import { cn } from "@/lib/utils";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useSidebarState } from "../hooks/useSidebarState";
import { SidebarHeader } from "../molecules/SidebarHeader";
import { SidebarNavItem } from "../molecules/SidebarNavItem";
import type { NavItem } from "../types";

interface NavigationSidebarProps {
  /** Building ID for scoped localStorage persistence */
  buildingId: string;
  /** Navigation items to display in the sidebar */
  items: NavItem[];
  /** Optional logo rendered at the top of the sidebar header */
  logo?: React.ReactNode;
  /** Optional render-prop for content rendered at the bottom of the sidebar (receives current isCollapsed) */
  footer?: (ctx: { isCollapsed: boolean }) => React.ReactNode;
  /** Optional render-prop for content rendered above the nav items (receives current isCollapsed) */
  headerSlot?: (ctx: { isCollapsed: boolean }) => React.ReactNode;
  /** Default collapsed state if no stored value exists (defaults to false) */
  defaultCollapsed?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Optional controlled collapse state (WHA-3109). When provided alongside
   * `onToggleCollapse`, the parent owns the state and the internal
   * `useSidebarState` hook is bypassed. Lets siblings (e.g. ChatWidget)
   * react to sidebar collapse changes.
   */
  isCollapsed?: boolean;
  /** Optional controlled toggle handler — pair with `isCollapsed` (WHA-3109). */
  onToggleCollapse?: () => void;
  /**
   * Controlled mode only: snap instead of animating (the owner's
   * `useSidebarState().skipTransition`, set on an automatic collapse). The
   * internal hook's flag cannot stand in for it — that instance is never
   * toggled, so once it auto-collapsed on mount it would suppress the
   * animation for good.
   */
  skipTransition?: boolean;
}

/**
 * NavigationSidebar - Organism component
 *
 * Complete standalone sidebar that composes all navigation atoms and molecules.
 * Manages its own collapse state via useSidebarState hook with localStorage persistence.
 * Provides TooltipProvider context for all child tooltips.
 *
 * Features:
 * - Standalone and plug-and-play - can be dropped into any layout
 * - Animated width transitions between collapsed/expanded states
 * - Per-building collapse state persistence
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * import { Home, Settings, FileText } from 'lucide-react';
 *
 * const navItems = [
 *   { id: 'home', label: 'Dashboard', icon: Home, href: '/dashboard' },
 *   { id: 'docs', label: 'Documents', icon: FileText, href: '/documents' },
 *   { id: 'settings', label: 'Settings', icon: Settings, href: '/settings', isActive: true },
 * ];
 *
 * <NavigationSidebar
 *   buildingId="building-123"
 *   items={navItems}
 *   logo={<img src="/logo.svg" alt="Logo" className="h-8 w-8" />}
 * />
 * ```
 */
export function NavigationSidebar({
  buildingId,
  items,
  logo,
  footer,
  headerSlot,
  defaultCollapsed = false,
  className,
  isCollapsed: controlledIsCollapsed,
  onToggleCollapse: controlledToggleCollapse,
  skipTransition: controlledSkipTransition,
}: NavigationSidebarProps) {
  const internal = useSidebarState({
    buildingId,
    defaultCollapsed,
  });
  const isCollapsed = controlledIsCollapsed ?? internal.isCollapsed;
  const toggleCollapse = controlledToggleCollapse ?? internal.toggleCollapse;
  const skipTransition =
    controlledIsCollapsed !== undefined
      ? (controlledSkipTransition ?? false)
      : internal.skipTransition;

  return (
    <TooltipProvider delayDuration={300} skipDelayDuration={100}>
      <aside
        className={cn(
          "flex h-full flex-col self-stretch overflow-hidden border-r bg-nav",
          !skipTransition && "transition-all duration-300",
          isCollapsed ? "w-16" : "w-60",
          className
        )}
      >
        <SidebarHeader isCollapsed={isCollapsed} onToggle={toggleCollapse} logo={logo} />
        {/*
          WHA-3249: top items + footer share a single scroll container so they
          shrink together when the sidebar runs out of vertical space (e.g. on
          highly-zoomed browsers / very short viewports). Previously the
          footer block had `shrink-0` while the top `<nav>` was the only
          `flex-1 overflow-y-auto` region — at high zoom the top icons were
          squeezed (and got their own scrollbar) while the bottom three
          stayed at full natural size, making them appear larger than the
          rest. Now both groups participate in the same overflow region.
          `mt-auto` on the footer keeps it pinned to the bottom whenever
          the inner content is shorter than the available space (the normal
          state), so the visual layout is unchanged at typical zoom.
        */}
        {/*
          Collapsed, the icon column must fit the window without a scrollbar:
          the rows shrink (down to a floor, see SidebarNavItem) instead of
          overflowing, and the section labels drop to a bare divider. A window
          too short even for that still scrolls by wheel or touch, but the bar
          is hidden — in a 64px column it eats a quarter of the width and
          clips the view switch's caption.
        */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
            isCollapsed && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          )}
        >
          {headerSlot && <div className="shrink-0 p-2">{headerSlot({ isCollapsed })}</div>}
          <nav className="flex min-h-0 flex-col gap-1 p-2">
            {items.map((item) => {
              if (item.isSectionLabel) {
                return (
                  <div key={item.id} className="relative shrink-0">
                    {/* The divider is drawn over a 1px spacer. Collapsed, it
                        moves to the vertical middle of the row, so it sits
                        halfway between the icons above and below instead of
                        above an empty label line. */}
                    <div
                      data-testid="nav-section-divider"
                      className={cn(
                        "absolute inset-x-0 border-t transition-[top] duration-200",
                        isCollapsed ? "top-[calc(50%-0.5px)]" : "top-0"
                      )}
                    />
                    <div className="mb-0.5 h-px" />
                    {/* Collapsed, the label's line closes up to a short gap
                        around the divider: the icon column has to fit the
                        window, and a faded-out label line is pure height. */}
                    <p
                      aria-hidden={isCollapsed || undefined}
                      className={cn(
                        "truncate px-3 text-xs font-semibold uppercase leading-4 tracking-wider text-muted-foreground transition-[opacity,height,padding] duration-200",
                        isCollapsed ? "h-1.5 py-0 opacity-0" : "h-[1.375rem] pb-0.5 pt-1"
                      )}
                    >
                      {item.label}
                    </p>
                  </div>
                );
              }
              // `icon` is required on every item that is not a section label.
              if (!item.icon) return null;
              return (
                <SidebarNavItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  href={item.href}
                  onClick={item.onClick}
                  isActive={item.isActive}
                  disabled={item.disabled}
                  restricted={item.restricted}
                  isCollapsed={isCollapsed}
                  showUnreadDot={item.showUnreadDot}
                />
              );
            })}
          </nav>
          {footer && (
            <div className="mt-auto flex min-h-0 flex-col gap-1 border-t p-2">
              {footer({ isCollapsed })}
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
